// design-system-exempt: the door is a designed surface rather than an app screen —
// a three-stop gradient on its own ink, which is not expressible as tokens and is
// documented where it is declared. The controls on it use --accent like everything else.
// The door, for both audiences: a team member arriving at their workspace, and a
// client opening the one page a team keeps for them.
//
// It is the first thing anyone sees of crum and, for a client, quite possibly the
// only thing — so it is composed rather than defaulted. The shape is a full-bleed
// split, borrowed from the Biobank Bridge build: a saturated panel carrying the
// identity and the claim, and a paper panel carrying the one thing you came to do.
// Nothing is centred in the middle of an empty page and nothing sits in a card.
//
// **The panel is crum's palette, not the workspace's accent.** Washing it in
// `--accent` was wrong twice over: a team that has not picked a colour gets the
// default, so the door came out in a blue that appears nowhere else in the product,
// and even a good accent would give every customer a differently-coloured front door
// for the same piece of software. The door is crum's; what is behind it is theirs.
// So: the dark warm ground and the ember of the landing page, with the team's mark
// and name on it. The one place their colour still shows is the button.
//
// At phone width it stacks: the panel becomes a short banner so the form is above
// the fold, because on a phone the thing you came to do should not be a scroll away.

import { useEffect, type ReactNode } from 'react'

import { blobUrl } from '../lib/api'
import type { Brand } from '../lib/brand'
import { Brand as Mark } from './Mark'

/** The top of the panel's gradient, and the colour the installed app's status-bar
 *  strip borrows while a door is open. One constant, because the two have to match
 *  exactly or the blur across the edge has something to show. */
const DOOR_INK = '#2a231c'

export function Doorway({
  brand,
  wordmark,
  headline,
  lede,
  children,
}: {
  brand: Brand
  /** top left of the colour panel: whose door this is */
  wordmark: string
  /** the claim, in the display serif */
  headline: ReactNode
  /** one or two supporting sentences */
  lede?: ReactNode
  /** the form */
  children: ReactNode
}) {
  /**
   * In an INSTALLED app, take the strip above the content with us.
   *
   * iOS 26 and 27 draw a scroll-edge blur across the top of the web view and there
   * is no CSS or meta opt-out (see index.html for what IS in our control: the status
   * bar style). But a blur only SHOWS where the two sides of the edge differ, and
   * this door is the widest gap in the product — a near-black panel under a strip
   * painted the app's paper rail. Same ink on both sides and there is nothing left
   * for the effect to reveal. The class also flips `color-scheme`, which is what iOS
   * reads to colour the status bar glyphs; the paper panel puts it back for itself.
   */
  useEffect(() => {
    // Only an installed copy has a strip to borrow. In a browser tab there is no
    // status bar of ours, so none of this applies and the page is left alone.
    if (!window.matchMedia('(display-mode: standalone)').matches) return
    const html = document.documentElement
    const meta = document.querySelector('meta[name="theme-color"]')
    const wasColor = meta?.getAttribute('content') ?? null
    // `color-scheme` is written INLINE on <html> before first paint (see the boot
    // script in index.html), and an inline style beats any rule — so this one has to
    // be set the same way rather than from the stylesheet.
    const wasScheme = html.style.colorScheme
    html.classList.add('at-the-door')
    html.style.colorScheme = 'dark'
    meta?.setAttribute('content', DOOR_INK)
    return () => {
      html.classList.remove('at-the-door')
      html.style.colorScheme = wasScheme
      if (wasColor !== null) meta?.setAttribute('content', wasColor)
    }
  }, [])

  return (
    <div className="door-scroll grid min-h-dvh lg:grid-cols-[1.05fr_0.95fr]">
      {/* ── the identity ──────────────────────────────────────────────────── */}
      <section
        className="relative flex flex-col justify-between overflow-hidden px-7 pt-8 pb-8 sm:px-10 lg:px-14 lg:pt-12 lg:pb-12"
        style={{
          // The landing page's ink, warmed toward ember along the diagonal. Three
          // stops rather than two: a flat fill reads as a placeholder and a
          // two-stop ramp on this hue banded visibly on a phone.
          background: `linear-gradient(152deg, ${DOOR_INK} 0%, #1d1813 48%, #3a2113 100%)`,
          color: '#f2eee4',
        }}
      >
        {/* The same dot grid the app's ground uses, at low alpha: the door and the
            product are the same drafting table. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(rgba(242,238,228,0.13) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />

        <div className="relative flex items-center gap-2.5">
          {brand.logoKey ? (
            <img
              src={blobUrl(brand.logoKey)}
              alt=""
              className="h-8 w-8 shrink-0 rounded-[30%] bg-white/95 object-contain p-0.5"
            />
          ) : (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[30%] bg-white/95">
              <Mark size={20} />
            </span>
          )}
          <span className="text-lead truncate font-semibold tracking-tight">{wordmark}</span>
        </div>

        {/* The one ember mark on the dark side: enough to tie the door to the
            landing page without colouring the whole panel. */}
        <span
          aria-hidden
          className="relative mt-5 hidden h-[3px] w-12 rounded-full lg:block"
          style={{ background: '#a8501f' }}
        />

        <div className="relative max-w-[30rem] py-10 lg:py-0">
          <h1
            className="text-[clamp(1.75rem,4.6vw,2.6rem)] leading-[1.08] font-semibold"
            style={{ fontFamily: 'var(--display)', letterSpacing: '-0.02em' }}
          >
            {headline}
          </h1>
          {lede ? (
            <p
              className="text-lead mt-4 leading-relaxed"
              style={{ color: 'rgba(242,238,228,0.74)' }}
            >
              {lede}
            </p>
          ) : null}
        </div>
      </section>

      {/* ── the way in ────────────────────────────────────────────────────── */}
      <section
        className="door-paper flex items-center justify-center px-7 py-12 sm:px-10 lg:px-14"
        style={{ background: 'var(--panel)' }}
      >
        <div className="w-full max-w-[22rem]">{children}</div>
      </section>
    </div>
  )
}

/** The form's own heading, in the display serif, so the two panels rhyme. */
export function DoorHeading({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <>
      <h2
        className="text-page font-semibold"
        style={{ fontFamily: 'var(--display)', letterSpacing: '-0.015em' }}
      >
        {children}
      </h2>
      {note ? (
        <p className="text-body mt-2.5 leading-relaxed" style={{ color: 'var(--muted)' }}>
          {note}
        </p>
      ) : null}
    </>
  )
}

/** One label above a field: small, tracked, uppercase. */
export function DoorLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-micro mb-1.5 block font-semibold uppercase"
      style={{ letterSpacing: '0.12em', color: 'var(--faint)' }}
    >
      {children}
    </label>
  )
}

/** The hairline and the small print under the form. */
export function DoorFinePrint({ children }: { children: ReactNode }) {
  return (
    <>
      <hr className="my-6 border-0 border-t" style={{ borderColor: 'var(--border)' }} />
      <p className="text-meta leading-relaxed" style={{ color: 'var(--muted)' }}>
        {children}
      </p>
    </>
  )
}

export const doorField: React.CSSProperties = {
  borderColor: 'color-mix(in srgb, var(--fg) 15%, transparent)',
  background: 'var(--bg)',
  color: 'var(--fg)',
}

export const doorButton: React.CSSProperties = {
  background: 'var(--accent)',
  color: 'var(--accent-contrast)',
}
